import { StringObj, Params } from '@/libs/types';
import { axios } from '@/libs/api.request';
import { Response } from '@/types/request.d';

// 转后端对应的key
const keyPath: StringObj = {
    loginName: 'login_name',
    name: 'user_name',
    email: 'email',
    phone: 'phone',
    status: 'remember',
    oldPassword: 'old_password',
    newPassword: 'new_password',
    career: 'job_key',
    industry: 'industry_key',
    company: 'profile_company_name',
};

// 登录
export const login = ({
    loginName,
    password,
    code,
    status,
}: Params) => {
    const data = {
        login_name: loginName,
        password,
        code,
        remember: status,
    };
    return axios.request({
        url: 'sys/user/passport/login',
        data,
        method: 'post',
    });
};

// 获取用户信息
export const getUserInfo = () => axios.request({
    url: 'sys/user/profile/get-login-info',
    method: 'get',
});

type UserBaseInfo = {
    translate_language: string;
}
// 获取用户部分字段信息（无缓存版）
// 目前只有用户设置的翻译语言
export const getUserBaseInfo = () => axios.request<Response<UserBaseInfo>>({
    url: 'sys/user/user/get-user-info',
    method: 'get',
});

// 获取验证码
export const getCode = () => axios.request({
    url: '/sys/user/passport/send-validate-code',
    method: 'post',
});

// 退出登录
export const logout = () => axios.request({
    url: 'sys/user/passport/logout',
    method: 'post',
});

// 注册
export const register = ({
    name, email, phone, password, code,
}: Params) => {
    const data: any = {
        user_name: name,
        password,
        code,
    };
    if (email) {
        data.email = email;
    }
    if (phone) {
        data.phone = phone;
    }
    return axios.request({
        url: 'sys/user/passport/register',
        data,
        method: 'post',
    });
};

// 发送邮箱注册验证码
export const sendRegEmail = (email: string) => {
    const data = {
        email,
    };
    return axios.request({
        url: 'sys/user/passport/send-reg-email',
        data,
        method: 'post',
    });
};

// 发送手机注册验证码
export const sendRegMessage = (phone: number) => {
    const data = {
        phone,
    };
    return axios.request({
        url: 'sys/user/passport/send-reg-phone',
        data,
        method: 'post',
    });
};

// 获取详细资料
export const getProfile = () => axios.request({
    url: 'sys/user/profile/get-profile',
    method: 'get',
});

// 发送绑定邮箱的验证码
export const sendBindEmail = (email: string) => axios.request({
    url: 'sys/user/profile/send-bind-email',
    method: 'post',
    data: {
        email,
    },
});

// 绑定邮箱
export const bindEmail = ({
    email,
    code,
}: Params) => axios.request({
    url: 'sys/user/profile/bind-email',
    method: 'post',
    data: {
        email,
        code,
    },
});

// 发送绑定手机的验证码
export const sendBindPhone = (phone: number) => axios.request({
    url: 'sys/user/profile/send-bind-phone',
    method: 'post',
    data: {
        phone,
    },
});

// 绑定手机
export const bindPhone = ({ phone, code }: Params) => axios.request({
    url: 'sys/user/profile/bind-phone',
    method: 'post',
    data: {
        phone,
        code,
    },
});

// 更新用户料资
export const updateProfile = (params: Params) => {
    const keyArr = ['name', 'oldPassword', 'newPassword', 'career', 'industry', 'company'];
    const data: any = {};
    Object.keys(params).map((i) => {
        const isHas = keyArr.some((item: any) => item === i);
        if (isHas) {
            data[keyPath[i]] = params[i];
        }
        return i;
    });
    return axios.request({
        url: 'sys/user/profile/update-profile',
        method: 'post',
        data,
    });
};

// 忘记密码发送短信
export const sendResetPasswordPhone = (phone: number) => axios.request({
    url: 'sys/user/passport/send-reset-password-phone',
    method: 'post',
    data: {
        phone,
    },
});

// 忘记密码验证短信的验证码
export const verifyPasswordCode = ({
    phone,
    code,
}: Params) => axios.request({
    url: 'sys/user/passport/verify-password-code',
    method: 'post',
    data: {
        phone,
        code,
    },
});

// 忘记密码发送重置密码邮件
export const sendResetPasswordEmail = ({
    email,
    url,
}: Params) => axios.request({
    url: 'sys/user/passport/send-reset-password-email',
    method: 'post',
    data: {
        email,
        url,
    },
});

// 重置密码最后一步提交新密码
export const updateResetPassword = ({
    password,
    token,
    type,
}: Params) => axios.request({
    url: 'sys/user/passport/update-reset-password',
    method: 'post',
    data: {
        password,
        token,
        type,
    },
});

// 接受企业邀请
export const updateResetGroup = ({
    token,
    type,
}: Params) => axios.request({
    url: 'sys/user/passport/update-reset-group',
    method: 'post',
    data: {
        token,
        type,
    },
});

interface UpdateParams {
    teamName: string;
    wechat?: string;
    qq?: string;
    skype?: string;
}

// 检查是否已经提交过申请
export const checkApplication = () => axios.request({
    url: 'sys/user/user/check-application',
    method: 'get',
});

// 升级套餐
export const updateApplication = ({
    teamName,
    wechat,
    qq,
    skype,
}: UpdateParams) => {
    const data: any = {
        company_name: teamName,
    };
    if (wechat) {
        data.wechat = wechat;
    }
    if (qq) {
        data.qq = qq;
    }
    if (skype) {
        data.skype = skype;
    }
    return axios.request({
        url: 'sys/user/user/update-application',
        method: 'post',
        data,
    });
};

interface AutoUpdateParams {
    months: number;
    projectName: string;
    companyType: string;
    userNum: string | number;
    paymentChannelName: string;
    trailDay: number;
    contact: string;
    payChannelName?: string;
    payType?: string;
    couponCode?: string;
}

// 自助升级
export const autoUpdateApplication = ({
    projectName,
    companyType,
    userNum,
    months,
    paymentChannelName,
    trailDay,
    contact,
    payChannelName,
    payType,
    couponCode,
}: AutoUpdateParams) => {
    const data: any = {
        project_name: projectName,
        company_type: companyType,
        product_name: 'Adspower',
        currency_code: 'USD',
        pay_type: payType || 'alipay',
        package_month: months,
        pay_channel_name: payChannelName,
        payment_channel_name: paymentChannelName || 'wechat',
        trail_day: trailDay || '',
        user_num: userNum,
        contact,
        coupon_code: couponCode || '',
    };
    return axios.request({
        url: 'sys/user/user/application',
        method: 'post',
        data,
    });
};

interface UpdatePackageParams {
    type: string;
    userNum: string | number;
    paymentChannelName?: string;
    currencyCode?: string;
    productName?: string;
    payType?: string;
    payChannelName?: string;
}

// 自助更改套餐
export const updatePackage = ({
    type,
    userNum,
    paymentChannelName,
    currencyCode,
    productName,
    payChannelName,
    payType,
}: UpdatePackageParams) => {
    const data: any = {
        type,
        user_num: userNum,
        pay_type: payType,
        pay_channel_name: payChannelName || '',
    };
    if (paymentChannelName) {
        data.payment_channel_name = paymentChannelName;
    }
    if (currencyCode) {
        data.currency_code = currencyCode;
    }
    if (productName) {
        data.product_name = productName;
    }
    return axios.request({
        url: 'sys/admin/company/update-package',
        method: 'post',
        data,
    });
};

interface paypalDate {
    payment_channel_name: string;
    paymentId: string;
    token: string;
    PayerID: string;
}

// paypal 支付
export const paypalFeature = ({
    payment_channel_name,
    paymentId,
    token,
    PayerID,
}: paypalDate) => axios.request({
    url: '/fbcc/payment/check-order',
    method: 'get',
    params: {
        payment_channel_name,
        paymentId,
        token,
        PayerID,
    },
});

interface feedbackParams {
    score: number;
    feedback: string;
    contact?: object;
}

// 提交意见反馈信息接口
export const submitFeedback = ({
    score,
    feedback,
    contact,
}: feedbackParams) => axios.request({
    url: '/sys/user/user/user-feedback',
    method: 'post',
    data: {
        score,
        feedback,
        contact: JSON.stringify(contact),
    },
});

interface checkAccountParams {
    user_name: string;
}
interface checkAccountRes {
    check_result: '1' | '0';// 校验结果（1 正常， 0 重复）
}
// 检查账号是否重复
export const checkAccount = (params: checkAccountParams) => axios.request<Response<checkAccountRes>>({
    url: '/sys/user/passport/check-account',
    method: 'get',
    params,
});

 interface VisitIpResData {
    country: string;
    region: string;
    city: string;
    'gmt-offset'?: string;
    timezone: string;
    latitude: string;
    longitude: string;
}

 interface VisitIpRes {
    data: VisitIpResData;
    count?: Object;
    code: number;
    msg: string;
}

 interface ResgisterGoogleResData {
    redirect_url: string;
    type:string;
}

 interface ResgisterGoogleRes {
    data: ResgisterGoogleResData;
    count: Object;
    code: number;
    msg: string;
}

export interface ResgisterGoogleCbData {
    state:string;
    code: string;
    scope:string;
    authuser:string;
    prompt:string
    domain?: string
}

interface GoogleRegisterCbData {
    cpl: string;
    plugin_bind_status: string;
    redirect_url: string;
    scene: 'register' | 'login';
    plugin_id?: string;
    sys_user_id: string;
    verify_key?: string;
}

export interface GoogleRegisterCb {
  data: GoogleRegisterCbData;
  count: Object;
  code: number;
  msg: string;
}

interface PhoneCodeParams {
    phone: string;
    verify_ticket: string;
    verify_randstr: string;
    type: string;
}

interface KycVerifyParams {
    name: string;
    id_number: string;
    phone: string;
    verification_code: string;
    face_verify_sign?: string; // 重新获取二维码时，给后端的签名（md5('phone'-'code')）
}

/**
 * 获取访问地址
 */
export const getVisitIp = () => axios.request<VisitIpRes>({
    url: '/sys/tools/tools/get-ip',
    method: 'get',
});

/**
 * Google注册
 */
export const resgisterGoogle = () => axios.request<ResgisterGoogleRes>({
    url: '/google/api/login-v1',
    method: 'get',
});

/**
 * @param data
 * 谷歌注册回调
 */
export const resgisterGoogleCallback = (data:ResgisterGoogleCbData) => axios.request<GoogleRegisterCb>({
    url: '/google/api/callback-v1',
    method: 'post',
    data,
});

/**
 * @param params
 * 绑定谷歌
 */
export const bindGoogle = (userId:number) => axios.request<ResgisterGoogleRes>({
    url: '/google/api/bind-v1',
    method: 'get',
    params: {
        id: userId,
    },
});

/**
 * @param data
 * 绑定谷歌回调
 */
export const bindGoogleCallback = (data:ResgisterGoogleCbData) => axios.request({
    url: '/google/api/bind-callback-v1',
    method: 'post',
    data,
});

interface phoneRegisterSendCodeParams {
    phone: string,
    verify_ticket: string,
    verify_randstr: string
    type: string, // 类型（1 注册发送，3 绑定发送，5 忘记密码， 9 kyc验证）
}

/**
 * 注册发送手机验证码接口
 * @param data
 */
export const phoneRegisterSendCode = (data: phoneRegisterSendCodeParams) => axios.request({
    url: '/sys/user/passport/send-code',
    method: 'post',
    data,
});

/**
 * 发送绑定手机验证码接口
 * @param data
 */
export const bindPhoneSendCode = (data: phoneRegisterSendCodeParams) => axios.request({
    url: '/sys/user/profile/send-bind-code',
    method: 'post',
    data,
});

export interface bindPhoneCodeParams {
    phone:string,
    verify_code: string,
    password?: string
}

/**
 * 绑定手机接口
 * @param data
 */
export const bindPhoneCode = (data: bindPhoneCodeParams) => axios.request({
    url: '/sys/user/profile/bind-code',
    method: 'post',
    data,
});

// 发送手机验证码
export const getPhoneCode = (data:PhoneCodeParams) => axios.request({
    url: '/sys/user/passport/send-code',
    method: 'post',
    data,
});

interface FaceVerifyRes {
    code: number;
    data: {
        Url: string; // 生成的验证地址
        BizToken: string; // 请求识别token
        RequestId: string;
        expired_time: string; // Url地址有效时间（s）
    }
}
export interface FaceVerifyResultRes {
    sys_user_id: string, // 用户ID real_user_id
    project_id: string, // 项目ID
    errCode: string, // 错误码
    errMsg: string, // 错误信息
    idCard: string, // 填写的身份证号码
    name: string, // 填写的姓名
    ocrName: string, // 识别的姓名
    ocrIdCard: string, // 识别的身份证号码
    bizToken: string,
}

// 获取人脸识别 链接 接口
export const faceVerifyPost = (data: KycVerifyParams) => axios.request<FaceVerifyRes>({
    url: '/sys/user/profile/face-verify',
    method: 'post',
    data,
});

// 获取人脸识别 结果 接口
export const faceVerifyResultPost = (data: { biz_token: string }) => axios.request<{ code: number, data: FaceVerifyResultRes }>({
    url: '/sys/user/profile/face-verify-result',
    method: 'post',
    data,
    customTips: true,
});

interface GetSecretRes {
    code: number;
    count: object;
    data: {
        qr_code_text: string;
        secret: string;
    };
    msg: string;
}

export interface BindSecretRequest {
    code?: string;
    project_id?: string;
    secret?: string;
}

export interface BindSecretRes {
    code: number;
    count: object;
    data: object;
    msg: string;
}

export interface CheckSecretRequest {
    code?: string;
    id?: string;
    verify_key?: string;
}

export interface CheckSecretRes {
    code: number;
    count: object;
    data: {
        cpl: string;
        id: string;
    };
    msg: string;
}

/**
 * 谷歌登录增加二次验证———获取二维码
 * @param data
 */
export const getTfaSecret = (data: { project_id: string }) => axios.request<GetSecretRes>({
    url: '/sys/user/tfa/get-tfa-secret',
    method: 'post',
    data,
});

/**
 * 登录增加二次验证———验证二维码
 * @param data
 */
export const bindTfaSecret = (data: BindSecretRequest) => axios.request<BindSecretRes>({
    url: '/sys/user/tfa/bind-tfa-secret',
    method: 'post',
    data,
    customTips: true,
});

export interface UnbindTfaSecretData {
    version: 'v2';
    code: string;
}

/**
 * 登录增加二次验证———停用二次验证
 * @param data
 */
export const unbindTfaSecret = (data: UnbindTfaSecretData) => axios.request<BindSecretRes>({
    url: '/sys/user/tfa/unbind-tfa-secret',
    method: 'post',
    data,
    customTips: true,
});

export interface PhoneAreasItem {
    iso_code: string;
    area_code: string;
}

/**
 * 获取手机区号接口
 * @returns
 */
export const getPhoneAreas = () => axios.request<Response<PhoneAreasItem[]>>({
    url: '/sys/user/passport/phone-areas',
    method: 'get',
});

export interface SwitchNewUiData {
    is_new_ui: '0' | '1' | '2' | '3'; // 0就是没有弹过窗的，1就是旧版，2就是新版, 3也是新版(第一次切换到新版)
}

/**
 * 切换新UI接口
 */
export const switchNewUi = (data: SwitchNewUiData) => axios.request<Response<{}>>({
    url: '/sys/user/user/switch-new-ui',
    method: 'post',
    data,
});
